<script setup lang="ts">
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { ElAlert } from "element-plus";
import LocationSelector from "../LocationSelector/LocationSelector.vue";
import { useScreenType } from "@/composables/useScreenType";
import { useRootStore } from "@/stores";
import { storeToRefs } from "pinia";
import CoreLink from "../CoreLink/CoreLink.vue";
import { useLocationSelector } from "@/composables/location";
import { useAuth } from "@/composables/auth";
import { useNotification } from "@/composables/useNotification";

export interface Props {
  title: string;
  subtitle?: string;
  showLocationSelector?: boolean;
}

withDefaults(defineProps<Props>(), {
  showLocationSelector: true,
});

const { isLocationManager } = useAuth();

const { t } = useI18n();
const route = useRoute();
const { isMobile } = useScreenType();
const { selectedLocation, isLocationsLoaded } = useLocationSelector();

const isSettingsPage = computed(() => route.path.includes("settings"));
const rootStore = useRootStore();
const { currentAlert } = storeToRefs(rootStore);
const { sendErrorNotification } = useNotification();

const shownMessages = new Set();

watch(
  currentAlert,
  (alert) => {
    if (!alert || !alert.isNotification) return;

    const messageTitle = alert.message.title;

    if (!shownMessages.has(messageTitle)) {
      sendErrorNotification(messageTitle, alert.isShowMessageSupport);
      shownMessages.add(messageTitle);
    }
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <i18n-t
    keypath="locationSelector.main"
    tag="div"
    scope="global"
    :class="[
      'page-title hide-on-print',
      {
        'has-subtitle': $slots.subtitle || subtitle,
        'page-title-mobile': isMobile && isLocationManager,
      },
    ]"
  >
    <template #title>
      <div :class="['container', { 'container-lm': isLocationManager }]">
        <component
          :is="isSettingsPage || isMobile ? 'h2' : 'h1'"
          :class="[
            'title',
            { 'title-h2': isSettingsPage, 'title-lm': isLocationManager },
          ]"
        >
          <i18n-t keypath="locationSelector.titleAt">
            <template #title>{{ title }}</template>
            <template #at>
              <span
                v-if="showLocationSelector && !isMobile"
                :class="['at', { 'at-h2': isSettingsPage }]"
              >
                {{ t("locationSelector.at") }}
              </span>

              <span
                v-if="
                  showLocationSelector &&
                  !isMobile &&
                  isLocationManager &&
                  isLocationsLoaded
                "
                :class="[
                  'at location',
                  { 'at-h2 space-location': isSettingsPage },
                ]"
                >{{ selectedLocation.name }}</span
              >
            </template>
          </i18n-t>
        </component>
      </div>
    </template>

    <template #location>
      <div class="at-location">
        <span
          v-if="showLocationSelector && isMobile"
          class="at"
        >
          {{ t("locationSelector.at") }}
        </span>
        <span
          v-if="
            showLocationSelector &&
            isMobile &&
            isLocationManager &&
            isLocationsLoaded
          "
          :class="['location-mobile', { 'at-h2': isSettingsPage }]"
        >
          {{ selectedLocation.name }}
        </span>
        <LocationSelector
          :visible="showLocationSelector && !isLocationManager"
        />
      </div>
    </template>
  </i18n-t>
  <p
    v-if="$slots.subtitle || subtitle"
    class="page-subtitle"
  >
    <slot name="subtitle">
      {{ subtitle }}
    </slot>
  </p>
  <el-alert
    v-if="currentAlert && !currentAlert.isNotification"
    class="alert--control"
    :type="currentAlert.type"
    :title="currentAlert.message.title"
    show-icon
  >
    <span v-if="currentAlert.message.description">
      {{ currentAlert.message.description }}
      <CoreLink
        v-if="currentAlert.supportLink"
        target="_blank"
        rel="noopener noreferrer"
        :href="currentAlert.supportLink?.href"
        default="Link"
        type="primary"
        :underline="false"
      >
        {{ currentAlert.supportLink.text }}
      </CoreLink>
    </span>
  </el-alert>
</template>

<style lang="scss" scoped>
.page-title {
  display: flex;
  align-items: flex-start;
  margin: 25px 0 30px;
  overflow: hidden;

  @media screen and (max-width: $breakpoint-sm) {
    flex-direction: column;
    padding-bottom: 2px;
  }
}

.page-title.page-title-mobile {
  width: 100%;
}

.page-title.has-subtitle {
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.06rem;
  color: var(--so-intro-text-color);
  margin-bottom: 1.875rem;
  line-height: 1.5rem;
}

.title {
  margin: 8px 5px 19px 0;
  white-space: nowrap;

  @media screen and (max-width: $breakpoint-sm) {
    margin: 8px 2px 0 0;
  }
}

.title-lm {
  width: 100%;
  display: flex;
  gap: 5px;
}

.title-h2 {
  margin: 8px 0 0 0;
}

h1 {
  height: 100%;
  max-height: 35px;
  line-height: 1.5;
  white-space: nowrap;
}

h2 {
  font-size: 1.4375rem;
  line-height: 1.6;
}

.at-location {
  display: flex;
  min-width: 0;
  max-width: 100%;
  margin-top: 8px;
}

:where(h1) span {
  padding-bottom: 7px;
}

.at {
  font-size: 2rem;
  font-weight: var(--font-normal);

  @media screen and (max-width: $breakpoint-sm) {
    font-size: 1.4375rem;
    line-height: 1.6;
    margin-right: 5px;
    font-family: var(--font-family-headings);
    display: flex;
    flex-shrink: 0;
  }
}

.at-h2 {
  font-size: 1.4375rem;
  line-height: 1.6;
  font-family: var(--font-family-headings);
}

.container {
  display: flex;
  flex-direction: column;
}

.container-lm {
  width: 100%;
}

.page-alert {
  margin-bottom: 1.875rem;
}

.location {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: -1px 0 -9px 5px;
}

.space-location {
  margin-left: 0;
}

.location-mobile {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 2rem;
  font-weight: var(--font-normal);

  @media screen and (max-width: $breakpoint-sm) {
    font-size: 1.4375rem;
    line-height: 1.6;
    margin-right: 5px;
    font-family: var(--font-family-headings);
  }
}

.alert--control {
  margin-bottom: 25px;
}
</style>
